import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, ConfigProvider, Modal, theme as antdTheme } from 'antd';
import { askForPlan, cancelPlanRequest, getPlanCatalog, getPlanRequests } from '../../services/PlanRequestsService';
import { formatBytes } from '../../services/SandboxService';
import { formatRelativeTime } from '../../utils/relativeTime';
import { usePhone } from '../../hooks/useMediaQuery';
import { CreditsSummary, EstimateNote, PlanCompareTable, PlanConfirm } from './PlanCompare';
import useCredits from '../../hooks/useCredits';
import { planLanguage, planText } from './planCompareText';
import './PlanRequest.css';

// What a plan's limits say, in the order of the usage meters; null = no limit.
export const LIMIT_ROWS = [
  { key: 'cpu', label: 'CPU', format: (v) => `${v} vCPU` },
  { key: 'memoryBytes', label: 'Memory', format: formatBytes },
  { key: 'diskBytes', label: 'Code disk', format: formatBytes },
  { key: 'dataDiskBytes', label: 'Data disk', format: formatBytes },
  { key: 'runningServices', label: 'Services at once', format: (v) => String(v) },
  { key: 'activeHoursPerMonth', label: 'Active hours a month', format: (v) => `${v} h` },
];
const FEATURE_LABELS = { chatOrgModels: 'Chat with the company’s models', chatOwnKey: 'Chat with your own key', playbooks: 'Playbooks' };
const CONNECTION_LABELS = { aiFoundry: 'AI Foundry', contextCatalog: 'Context Catalog', console: 'Console', fastData: 'Fast Data' };

/** A limit as text: "Unlimited" when the plan sets it to none (null), a dash when the plan does not have it. */
export function limitText(row, value) {
  if (value === undefined) { return '—'; }
  return value === null ? 'Unlimited' : row.format(value);
}

/** What a plan adds on top of the limits: its features and connections that are on. */
export function planExtras(plan) {
  const on = (flags, labels) => Object.entries(flags || {}).filter(([key, value]) => value && labels[key]).map(([key]) => labels[key]);
  return [...on(plan.features, FEATURE_LABELS), ...on(plan.connections, CONNECTION_LABELS)];
}

/** The line under the meters: the pending request, else the last decision of the past week, else nothing. */
export function requestStatus(data, now = Date.now()) {
  if (!data) { return null; }
  if (data.pending) {
    return { tone: 'pending', text: `You asked for the ${data.pending.to} plan ${formatRelativeTime(data.pending.createdAt)}: waiting for your sandbox admin.`, cancel: data.pending.id };
  }
  const last = (data.recent || []).find((r) => r.status === 'approved' || r.status === 'rejected');
  if (!last || now - Date.parse(last.decidedAt) > 7 * 24 * 3600_000) { return null; }
  const note = last.note ? ` “${last.note}”` : '';
  return last.status === 'approved'
    ? { tone: 'ok', text: `Your request for ${last.to} was approved${last.applied ? '' : ': it applies the next time your sandbox wakes up'}.${note}` }
    : { tone: 'warn', text: `Your request for ${last.to} was not approved.${note}` };
}

/**
 * "Change plan": the plans side by side with their estimated cost per hour (PlanCompare.jsx, GET /api/v1/me/plans),
 * then a confirmation that says what the change means and sends the request. Where the gateway has no plan catalog
 * (an older one) or it cannot be read, the list of the other plans to pick from, as before.
 */
function PlanDialog({ data, onClose, onSent, open }) {
  const others = (data?.plans || []).filter((p) => p.name !== data?.current?.name);
  const [choice, setChoice] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // undefined: loading; null: no catalog here (the list below); else the gateway's plans side by side.
  const [catalog, setCatalog] = useState(undefined);
  const [chosen, setChosen] = useState(null);
  const phone = usePhone();
  const { me: credits } = useCredits();
  const lang = planLanguage();
  const t = planText(lang);
  useEffect(() => {
    if (!open) { return undefined; }
    setChoice(others[0]?.name || null); setReason(''); setError(null); setChosen(null); setCatalog(undefined);
    let live = true;
    Promise.resolve()
      .then(() => getPlanCatalog())
      .then((next) => { if (live) { setCatalog(next && Array.isArray(next.plans) ? next : null); } })
      .catch(() => { if (live) { setCatalog(null); } });
    return () => { live = false; };
    // Only when it opens: the plans do not change while it is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ask = async(name) => {
    if (!name) { return; }
    setBusy(true);
    setError(null);
    try {
      onSent(await askForPlan(name, reason));
    } catch (err) {
      setError(err.message || t('sendError'));
    } finally {
      setBusy(false);
    }
  };
  const send = (event) => { event.preventDefault(); ask(choice); };

  const current = data?.current;
  const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  return (
    <ConfigProvider theme={{ algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm }}>
      <Modal
        className={catalog ? 'sbx-plan-dialog sbx-plan-compare' : 'sbx-plan-dialog'}
        destroyOnHidden
        footer={null}
        mask={{ closable: !busy }}
        onCancel={busy ? undefined : onClose}
        open={open}
        title={t('title')}
        width={catalog && !chosen ? 1040 : 760}
      >
        {catalog === undefined && <p aria-busy="true" className="sbx-pc-loading">{t('loading')}</p>}
        {catalog && chosen && (
          <PlanConfirm
            busy={busy}
            catalog={catalog}
            error={error}
            lang={lang}
            onBack={() => { setChosen(null); setError(null); }}
            onReason={setReason}
            onSend={() => ask(chosen.name)}
            plan={chosen}
            reason={reason}
          />
        )}
        {catalog && !chosen && (
          <>
            <p className="sbx-pc-intro">{catalog.current?.name ? `${t('introOn', { plan: catalog.current.name })} ` : ''}{t('intro')}</p>
            <CreditsSummary credits={credits} lang={lang} />
            <PlanCompareTable busy={busy} catalog={catalog} credits={credits} lang={lang} onChoose={setChosen} phone={phone} />
            <EstimateNote catalog={catalog} lang={lang} />
          </>
        )}
        {catalog === null && (
        <form noValidate onSubmit={send}>
          <p className="sbx-plan-hint">
            {current?.name ? `You are on ${current.name}. ` : ''}
            Your sandbox admin decides; you get the answer in Flow’s notifications. Files and databases are kept.
          </p>
          {data?.pending && (
            <p className="sbx-plan-replace" role="note">This replaces your pending request for {data.pending.to}.</p>
          )}
          <fieldset className="sbx-plan-choices">
            <legend className="sr-only">Plan</legend>
            {others.map((plan) => (
              <label className={`sbx-plan-choice${choice === plan.name ? ' selected' : ''}`} key={plan.name}>
                <input checked={choice === plan.name} disabled={busy} name="plan" onChange={() => setChoice(plan.name)} type="radio" value={plan.name} />
                <span className="sbx-plan-name">{plan.name}</span>
                <dl className="sbx-plan-limits">
                  {LIMIT_ROWS.map((row) => {
                    const value = plan.limits?.[row.key];
                    const mine = current?.limits?.[row.key];
                    const more = value === null ? typeof mine === 'number' : typeof mine === 'number' && typeof value === 'number' && value > mine;
                    const less = typeof value === 'number' && (mine === null || (typeof mine === 'number' && value < mine));
                    return (
                      <div className="sbx-plan-limit" key={row.key}>
                        <dt>{row.label}</dt>
                        <dd className={more ? 'more' : less ? 'less' : undefined}>{limitText(row, value)}</dd>
                      </div>
                    );
                  })}
                </dl>
                {planExtras(plan).length > 0 && <span className="sbx-plan-extras">{planExtras(plan).join(' · ')}</span>}
              </label>
            ))}
          </fieldset>
          <label className="sbx-plan-label" htmlFor="sbx-plan-reason">Why (optional)</label>
          <textarea
            className="sbx-plan-reason"
            disabled={busy}
            id="sbx-plan-reason"
            maxLength={1000}
            onChange={(event) => setReason(event.target.value)}
            placeholder="e.g. I run a database and three services for a demo next week"
            rows={3}
            value={reason}
          />
          {error && <p className="sbx-plan-error" role="alert">{error}</p>}
          <div className="sbx-plan-actions">
            <Button disabled={busy} onClick={onClose}>Cancel</Button>
            <Button disabled={!choice} htmlType="submit" loading={busy} type="primary">{busy ? 'Sending…' : `Ask for ${choice || 'a plan'}`}</Button>
          </div>
        </form>
        )}
      </Modal>
    </ConfigProvider>
  );
}

PlanDialog.propTypes = { data: PropTypes.object, onClose: PropTypes.func.isRequired, onSent: PropTypes.func.isRequired, open: PropTypes.bool };

/**
 * "Ask for another plan" next to the usage meters, and the state of the request under them. Nothing shows where the
 * gateway has no plan requests (shared mode, an older gateway) or there is no other plan to ask for.
 */
export const PLAN_REQUESTS_CHANGED = 'flow:plan-requests-changed';
const announceChange = () => { window.dispatchEvent(new CustomEvent(PLAN_REQUESTS_CHANGED)); };

export function usePlanRequests({ retryMs = 15000 } = {}) {
  const [data, setData] = useState(null);
  // A failed load (signed in a moment later, the gateway restarting, the sandbox still waking) keeps the last answer
  // and is tried again: the first answer alone used to decide, so one failure hid the button until a reload.
  const load = useCallback(() => getPlanRequests().then(setData).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  // A request sent or cancelled from another button on the page (PlanChangeButton): every copy reads it again.
  useEffect(() => {
    window.addEventListener(PLAN_REQUESTS_CHANGED, load);
    return () => window.removeEventListener(PLAN_REQUESTS_CHANGED, load);
  }, [load]);
  useEffect(() => {
    if (data || !retryMs) { return undefined; }
    const timer = setInterval(() => { if (document.visibilityState === 'visible') { load(); } }, retryMs);
    return () => clearInterval(timer);
  }, [data, load, retryMs]);
  return [data, setData, load];
}

export function AskPlanButton({ data, onOpen }) {
  if (!data || !(data.plans || []).some((p) => p.name !== data.current?.name)) { return null; }
  const t = planText();
  return <Button onClick={onOpen} size="small">{data.pending ? t('changeRequest') : t('changePlan')}</Button>;
}

AskPlanButton.propTypes = { data: PropTypes.object, onOpen: PropTypes.func.isRequired };

/**
 * "Change plan" wherever the sandbox shows its plan or a limit is reached (the usage strip, the limit warnings, a start
 * refused by the plan, Settings, the waking card): opens the same "Change plan" dialog as Home's plan card.
 * Self-contained (its own plan requests; one request shared with the other buttons on the page). Nothing shows where
 * the gateway has no plan requests or there is no other plan; `fallback` (e.g. "ask your sandbox admin") then shows.
 */
export function PlanChangeButton({ label = null, size = 'small', type = 'default', fallback = null, className = undefined }) {
  const [data, setData] = usePlanRequests();
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  if (!data || !(data.plans || []).some((p) => p.name !== data.current?.name)) { return fallback; }
  const t = planText();
  const text = label || (data.pending ? t('changeRequest') : t('changePlan'));
  return (
    <span className={className ? `sbx-plan-change ${className}` : 'sbx-plan-change'}>
      <Button onClick={() => setOpen(true)} size={size} type={type}>{text}</Button>
      {sent && data.pending && <span className="sbx-plan-sent" role="status">{t('sent', { plan: data.pending.to })}</span>}
      <PlanDialog
        data={data}
        onClose={() => setOpen(false)}
        onSent={(next) => { setData(next); setSent(true); setOpen(false); announceChange(); }}
        open={open}
      />
    </span>
  );
}

PlanChangeButton.propTypes = {
  label: PropTypes.string,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
  type: PropTypes.string,
  fallback: PropTypes.node,
  className: PropTypes.string,
};

/**
 * The plan the person is on, from one source of truth: the sandbox admin's (plan requests' `current`, which the admin
 * page shows too), then the overview's `plan.name` (which the gateway also takes from the admin; an older gateway gave
 * the plan the pod was started with). `appliesOnWake`: the running sandbox still has its previous plan's limits.
 */
export function currentPlan(sandbox, planRequests) {
  const name = planRequests?.current?.name || sandbox?.plan?.name || null;
  const running = sandbox?.plan?.running?.name ?? null;
  const differs = Boolean(sandbox?.plan?.appliesOnWake)
    || Boolean(name && sandbox?.plan?.name && sandbox.plan.name.toLowerCase() !== name.toLowerCase());
  return { name, appliesOnWake: differs, runningName: running || (differs ? sandbox?.plan?.name || null : null) };
}

/** "Team plan" as a pill, from currentPlan; its title says when the running sandbox still has the previous plan. */
export function PlanPill({ sandbox = null }) {
  const [planRequests] = usePlanRequests();
  const plan = currentPlan(sandbox, planRequests);
  if (!plan.name) { return null; }
  const title = plan.appliesOnWake
    ? `Your sandbox still runs with ${plan.runningName ? `the ${plan.runningName} plan’s` : 'its previous'} limits until it next sleeps and wakes.`
    : undefined;
  return <span className="sbx-pill" title={title}>{plan.name} plan</span>;
}

PlanPill.propTypes = { sandbox: PropTypes.object };

export function PlanRequestStatus({ data, onChange }) {
  const [busy, setBusy] = useState(false);
  const status = requestStatus(data);
  if (!status) { return null; }
  const cancel = async() => {
    setBusy(true);
    try { onChange(await cancelPlanRequest(status.cancel)); } catch { /* stays as it was */ } finally { setBusy(false); }
  };
  return (
    <p className={`sbx-plan-status ${status.tone}`} role="status">
      {status.text}
      {status.cancel && <Button disabled={busy} onClick={cancel} size="small" type="link">Cancel request</Button>}
    </p>
  );
}

PlanRequestStatus.propTypes = { data: PropTypes.object, onChange: PropTypes.func.isRequired };

export default PlanDialog;
